import { useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { Button } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { AcceptTenderDialog } from './AcceptTenderDialog'
import { RejectTenderDialog } from './RejectTenderDialog'
import { TenderDetails } from './TenderDetails'
import { type TenderCarrier, type TenderOffer } from './types'

const ToastShowButton = styled(Button)`
  background-color: ${toCSSValue('color-background-highlight')} !important;
  color: ${toCSSValue('color-text-link')} !important;
`

const ToastContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export type TenderCardProps = {
  tender: TenderOffer
  disabled?: boolean
  carrier?: TenderCarrier
  highlighted?: boolean
}

function TenderAcceptedToast({ onOpenShipment }: { onOpenShipment: () => void }) {
  return (
    <ToastContent>
      <span>Submitted with success</span>
      <ToastShowButton
        variant="tertiary"
        onClick={e => {
          e.stopPropagation()
          onOpenShipment()
        }}
      >
        Open shipment
      </ToastShowButton>
    </ToastContent>
  )
}

export function TenderCard({ tender, disabled, carrier, highlighted }: TenderCardProps) {
  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  function handleAcceptClick() {
    setIsAcceptDialogOpen(true)
  }

  function handleRejectClick() {
    setIsRejectDialogOpen(true)
  }

  async function handleAcceptConfirm() {
    setIsAcceptDialogOpen(false)
    setIsSubmitting(true)
    try {
      await Promise.resolve()
      toast.success(<TenderAcceptedToast onOpenShipment={() => () => {}} />)
    } catch {
      toast.error('Action failed. Try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleAcceptCancel() {
    setIsAcceptDialogOpen(false)
  }

  async function handleRejectConfirm(reason: string) {
    setIsRejectDialogOpen(false)
    setIsSubmitting(true)
    try {
      await Promise.resolve()
      toast.success('Submitted with success')
    } catch {
      toast.error('Action failed. Try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleRejectCancel() {
    setIsRejectDialogOpen(false)
  }

  return (
    <>
      <TenderDetails
        key={tender.id}
        id={tender.id}
        tender={tender}
        carrier={carrier}
        disabled={disabled}
        onAcceptClick={handleAcceptClick}
        onRejectClick={handleRejectClick}
        highlighted={highlighted}
      />
      <AcceptTenderDialog
        tender={tender}
        open={isAcceptDialogOpen}
        onConfirm={handleAcceptConfirm}
        onCancel={handleAcceptCancel}
        loading={isSubmitting}
      />
      <RejectTenderDialog
        open={isRejectDialogOpen}
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
        loading={isSubmitting}
      />
    </>
  )
}
